import { useEffect, useState } from "react";
import { Loader2, Trash2, Upload } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { MobileField } from "@/components/SeaAddress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

function ImageSlot({ user, kind, label, has, onChanged }) {
  const [src, setSrc] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let url;
    setSrc(null);
    if (has) {
      api.get(`/users/${user.id}/images/${kind}`, { responseType: "blob" })
        .then(({ data }) => { url = URL.createObjectURL(data); setSrc(url); })
        .catch(() => setSrc(null));
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [user.id, kind, has]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "image/png") { toast.error("Only PNG images are allowed"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image is larger than 2 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { await api.post(`/users/${user.id}/images/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`${label} saved`); onChanged(); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/users/${user.id}/images/${kind}`); toast.success(`${label} removed`); onChanged(); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid={`user-image-slot-${kind}`}>
      <div className="text-sm font-medium text-slate-800">{label}</div>
      <div className="h-28 rounded-md bg-[repeating-conic-gradient(#f1f5f9_0%_25%,#fff_0%_50%)] bg-[length:16px_16px] border border-dashed border-slate-200 flex items-center justify-center">
        {busy ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : src
          ? <img src={src} alt={label} className="max-h-24 max-w-full object-contain" data-testid={`user-image-preview-${kind}`} />
          : <span className="text-xs text-slate-400" data-testid={`user-image-empty-${kind}`}>None uploaded</span>}
      </div>
      <div className="flex gap-2">
        <label className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
          <Upload className="w-3.5 h-3.5" /> {has ? "Replace" : "Upload"} PNG
          <input type="file" accept="image/png" className="hidden" onChange={upload} disabled={busy} data-testid={`user-image-input-${kind}`} />
        </label>
        {has && <Button type="button" size="sm" variant="ghost" className="text-rose-600" onClick={remove} disabled={busy} data-testid={`user-image-remove-${kind}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
      </div>
    </div>
  );
}

function AlertSettings({ user, onChanged }) {
  const [mobile, setMobile] = useState(user.mobile || "");
  useEffect(() => { setMobile(user.mobile || ""); }, [user.id, user.mobile]);
  const save = async (body, msg) => {
    try { await api.patch(`/users/${user.id}/pipeline-settings`, body); toast.success(msg); onChanged(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3 sm:col-span-2" data-testid="user-pipeline-settings">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[180px] space-y-1">
          <MobileField label="Mobile number" value={mobile} onChange={setMobile} prefix="user-mobile" />
        </div>
        <Button type="button" size="sm" variant="outline" onClick={() => save({ mobile }, "Mobile saved")} data-testid="user-mobile-save-btn">Save</Button>
      </div>
      <label className="flex items-center justify-between gap-3 text-sm text-slate-700">
        <span>Can generate SEA contracts <span className="text-xs text-slate-400">(Docs Manager always can)</span></span>
        <Switch checked={!!user.can_generate_sea} onCheckedChange={(v) => save({ can_generate_sea: v }, v ? "SEA generation enabled" : "SEA generation disabled")} data-testid="user-sea-toggle" />
      </label>
    </div>
  );
}

export function UserImagesDialog({ user, onClose, onChanged }) {
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-images-dialog">
        <DialogHeader>
          <DialogTitle>Signature, stamp &amp; alerts</DialogTitle>
          <DialogDescription>{user?.first_name} {user?.last_name} — printed on SEA contracts this user generates. PNG, max 2 MB.</DialogDescription>
        </DialogHeader>
        {user && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ImageSlot user={user} kind="signature" label="Signature" has={user.has_signature_image} onChanged={onChanged} />
            <ImageSlot user={user} kind="stamp" label="Stamp (optional)" has={user.has_stamp_image} onChanged={onChanged} />
            <AlertSettings user={user} onChanged={onChanged} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
